import {
    setBookableResourcesQuery,
    useBookableResourcesQuery,
} from '../../lib/Queries';
import Loading from '../Loading';
import AddBookableResourceModal from './AddBookableResourceModal.jsx';
import React, { useEffect, useState } from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import DotDotDotMenu from '../DotDotDotMenu';
import TrashIcon from '../icons/TrashIcon.jsx';
import useDelete from '../../lib/useDelete.jsx';
import UppercaseIcon from '../icons/UppercaseIcon.jsx';
import Modal from '../Modal';
import TextInput from '../TextInput';
import ErrorMessage from '../ErrorMessage';
import { shouldShowCapacity } from '../../lib/BookableResourceHelpers.js';
import ShowTimezoneIfDifferent from './ShowTimezoneIfDifferent.jsx';
import ErrorMessageBlock from '../ErrorMessageBlock.jsx';
import SortableContainers from '../SortableContainers.jsx';
import IconSort from '../icons/IconSort.jsx';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import CustomTags from '~/components/customfields/CustomTags';
import { ObjectTypes } from '~/lib/ObjectTypes';

const BookableResourceRow = ({
    isSelected,
    onClick,
    resource,
    onClickEditName,
}) => {
    const { saveDelete, isDeleting } = useDelete(
        `/bookable-resources/${resource.id}/delete`,
    );

    function doDelete() {
        saveDelete().then(result => {
            setBookableResourcesQuery(result, true);
        });
    }

    return (
        <div className="pr-6 py-3 flex gap-4 items-center justify-between">
            <div
                className="flex-1 group cursor-pointer bookable-resource-row"
                onClick={onClick}
            >
                <div
                    className={`flex items-center group-hover:text-primary-700 ${isSelected && 'text-primary-700'}`}
                >
                    <IconSort className="cursor-grab text-gray-500 inline-block -ml-6 opacity-0 group-hover:opacity-100" />
                    {resource.name}
                </div>
                <CustomTags
                    type={ObjectTypes.BOOKABLE_RESOURCE}
                    id={resource.id}
                    fields={resource.custom_fields}
                />
                <div className="text-gray-500 text-sm">
                    {shouldShowCapacity(resource) && (
                        <span className="mr-1">{resource.capacity}</span>
                    )}
                    {resource.type !== 'Person' && (
                        <>
                            {resource.type}
                            {resource.capacity > 1 ? 's' : ''}
                        </>
                    )}
                    <ShowTimezoneIfDifferent
                        timezone={resource.timezone}
                        className="text-sm text-gray-500 inline"
                    />
                </div>
                {!resource.event_types?.length && (
                    <ErrorMessage>
                        No event types are enabled for {resource.name}, please edit and select which event types can be booked.
                    </ErrorMessage>
                )}
            </div>
            <DotDotDotMenu
                options={[
                    {
                        name: 'Edit',
                        icon: <Cog6ToothIcon />,
                        onClick: () => onClickEditName(resource),
                    },
                    {
                        name: isDeleting ? 'Deleting...' : 'Delete',
                        isDelete: true,
                        onClick: doDelete,
                    },
                ]}
            />
        </div>
    );
};

const SortableResourceList = ({
    resources,
    onReorder,
    selected,
    onClickEditName,
    setSelected,
}) => {
    return (
        <SortableContainers
            items={{ 0: resources }}
            setItems={newItems => onReorder(newItems[0])}
            renderItem={({ item, isActive }) => (
                <BookableResourceRow
                    key={item.id}
                    resource={item}
                    onClick={() => setSelected && setSelected(item)}
                    isSelected={selected?.id === item.id}
                    onClickEditName={onClickEditName}
                />
            )}
            Container={({ listId, idx, children }) => (
                <div className="divide-y ml-8 ">{children}</div>
            )}
        />
    );
};

const BookableResourcesList = ({
    setSelected,
    selected,
    onClickEditName,
    hideAddButton,
}) => {
    const { isLoading, data } = useBookableResourcesQuery(true);
    const bookableResources = data || [];

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [addType, setAddType] = useState('person');

    const { save: saveOrder, isSaving: isSavingOrder } = useBusinessPostApi(
        `/bookable-resources/reorder`,
    );
    const { isSaving, save } = useBusinessPostApi(`/bookable-resources/0`);

    useEffect(() => {
        if (bookableResources.length === 1) {
            setSelected(bookableResources[0]);
        }
    }, [bookableResources.length]);

    useEffect(() => {
        if (selected) {
            // Make sure selected has updated data if it changed!
            const foundResource = bookableResources.find(
                r => r.id === selected.id,
            );
            setSelected(foundResource);
        }
    }, [JSON.stringify(bookableResources)]);

    async function saveNewBookableResource({
        type,
        name,
        capacity,
        description,
    }) {
        const data = await save({
            name: name,
            type: type,
            capacity: capacity,
            description: description,
        });

        setBookableResourcesQuery(data.allBookableResources, true);
        setSelected(data.bookableResource);
    }

    function onReorder(newItems) {
        // Save order and update local cache too
        saveOrder({
            order: newItems.map(br => br.id),
        });
        setBookableResourcesQuery(newItems, true);
    }

    const people = bookableResources.filter(r => r.type === 'Person');
    const resources = bookableResources.filter(r => r.type !== 'Person');

    return (
        <div>
            {bookableResources.length === 0 && !isLoading && (
                <ErrorMessageBlock className="mb-4">
                    Clients are not able to book appointments right now. Please
                    add a Person or Resource below that clients can book with.
                </ErrorMessageBlock>
            )}
            <div className="panel-label">People</div>
            <div className="panel divide-y">
                {isLoading && <Loading className="p-4" />}
                <SortableResourceList
                    resources={people}
                    onReorder={newItems =>
                        onReorder([...newItems, ...resources])
                    }
                    selected={selected}
                    setSelected={setSelected}
                    onClickEditName={onClickEditName}
                />
                {bookableResources.length === 0 && !isLoading && (
                    <div className="p-4">
                        You have no people that can accept appointments
                    </div>
                )}
            </div>
            {!hideAddButton && (
                <button
                    type="button"
                    className={`btn-light-pill mt-3`}
                    onClick={() => {
                        setAddType('person');
                        setIsModalOpen(true);
                    }}
                >
                    Add Person
                </button>
            )}

            {(!hideAddButton || resources.length > 0) && (
                <>
                    <div className="panel-label mt-6">
                        Other Bookable Things
                    </div>
                    <div className="panel divide-y">
                        {isLoading && <Loading className="p-4" />}
                        <SortableResourceList
                            resources={resources}
                            onReorder={newItems =>
                                onReorder([...newItems, ...people])
                            }
                            selected={selected}
                            setSelected={setSelected}
                            onClickEditName={onClickEditName}
                        />
                    </div>
                    {!hideAddButton && (
                        <button
                            type="button"
                            className={`btn-light-pill ${resources.length > 0 || isLoading ? 'mt-3' : 'mt-0'}`}
                            onClick={() => {
                                setIsModalOpen(true);
                                setAddType('resource');
                            }}
                        >
                            Add Bookable
                        </button>
                    )}
                </>
            )}

            <AddBookableResourceModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                doSubmit={saveNewBookableResource}
                addType={addType}
            />
        </div>
    );
};

export default BookableResourcesList;
